import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { useAppDispatch } from "hooks/redux";
import { useHistory, useLocation } from "react-router";

import {
    PostMinimumSpending,
} from "api/rpc/2024-04/facilityAdmin/accounts/minimumSpending";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import SelectNew from "components/select/SelectNew";
import Input from "components/form/input";
import Toggle from "components/form/toggle/Toggle";
import Checkbox from "components/form/checkbox/Checkbox";

import { TCustomerType } from "redux/reducers/models/facility";
import { IDepartments, ICategories } from "redux/reducers/models/product";

interface INewMinimumState {
    title: string;
    amount: number;
    structure: "monthly" | "yearly" | "quarterly";
    account_ids: number[];
    apply_to_new_accounts: boolean;
    apply_to_new_account_customer_type_ids: number[];
    qualified_departments: number[];
}

export default function NewMinimumSpending() {

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const location = useLocation();
    const backToMinimumsUrl = `/admin/settings/accounts/minimum-spending${location.search || ""}`;

    const [state, setState] = useState<INewMinimumState>({
        title: "",
        amount: 0,
        structure: "monthly",
        account_ids: [],
        apply_to_new_accounts: true,
        apply_to_new_account_customer_type_ids: [],
        qualified_departments: [],
    });
    const [types, setTypes] = useState<TCustomerType[]>(undefined);
    const [departments, setDepartments] = useState<Array<IDepartments & {cateogries?: ICategories[]}>>(undefined);
 
    useEffect(() => {
        const source = axios.CancelToken.source();
        void loadCustomerTypes(source.token);
        void loadDepartments(source.token);
        return () => source.cancel();
    }, []);

    async function createMinimumSpend() {
        const res = await PostMinimumSpending(state, true);

        if (res.status !== StatusCode.OK) {
            dispatch(showError(typeof res.message === "string" ? res.message : t("Error creating minimum spend")));
            return;
        }

        history.push(backToMinimumsUrl);
    }

    async function loadCustomerTypes(token?: CancelToken) {
        const res = await GetCustomerType({ limit: 1000, application: "green_fee" }, true, token);

        if (token && token.reason) {
            return;
        }
        if (res.status !== StatusCode.OK) {
            dispatch(showError(typeof res.message === "string" ? res.message : t("Error creating minimum spend")));
            return;
        }

        setTypes(res.data);
    }

    async function loadDepartments(cancelToken?: CancelToken) {
        const res = await GetDepartments({extended: true}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setDepartments([]);
            dispatch(showError(t("Error getting departments")));
            return;
        }
        setDepartments(res?.data?.filter(department => department?.type === "department"));
    }

    function addType(type: TCustomerType){
        let updatedIds = [...state.apply_to_new_account_customer_type_ids];
        if (updatedIds?.includes(type?.id)) {
            updatedIds = updatedIds?.filter(updatedId => updatedId !== type?.id);
        } else {
            updatedIds = [...updatedIds, type?.id];
        }
        setState(prev => ({ ...prev, apply_to_new_account_customer_type_ids: updatedIds }));
    }

    function addDepartment(department: IDepartments & {cateogries?: ICategories[]} | ICategories){
        let updatedIds = [...state.qualified_departments];
        if (updatedIds?.includes(department?.id)) {
            //Remove department and all child categories
            if (department?.type === "department") {
                updatedIds = updatedIds?.filter(updatedId => updatedId !== department?.id && !department?.cateogries?.some(category => category?.id === updatedId));
            } else {
                //Remove category
                updatedIds = updatedIds?.filter(updatedId => updatedId !== department?.id);
            }
        } else {
            //Add department and all child categories
            if (department?.type === "department") {
                const allIds = [department?.id, ...department.cateogries.map(category => category?.id)];
                updatedIds = [...updatedIds, ...allIds];
            } else {
                //Add category
                updatedIds = [...updatedIds, department?.id];
            }
        }
        setState(prev => ({ ...prev, qualified_departments: updatedIds }));
    }

    const primaryAction = {
        content: t("Create"),
        action: () => void createMinimumSpend(),
    };

    return (
        <Page
            title={t("New Minimum Spending")}
            breadcrumbs={[
                {
                    prefix: true,
                    label: t("Minimums"),
                    url: backToMinimumsUrl,
                },
            ]}
            narrow
            primaryAction={primaryAction}
        >
            <Card>
                <Card.Section title={t("General Information")}>
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Title")}
                                value={state.title}
                                onChange={e => setState(prev => ({ ...prev, title: e.target.value }))}
                            />
                            <Input
                                label={t("Minimum Amount Spent")}
                                value={state.amount}
                                onChange={e => setState(prev => ({ ...prev, amount: Number(e.target.value) }))}
                                type={"number"}
                                min={0}
                                prefix={"$"}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <SelectNew
                                id="new-minimum-structure-select"
                                value={state.structure}
                                label={t("Structure")}
                                options={[
                                    { id: "monthly", label: t("Monthly") },
                                    { id: "yearly", label: t("Yearly") },
                                    { id: "quarterly", label: t("Quarterly") },
                                ]}
                                onChange={value => setState(prev => ({ ...prev, structure: value }))}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Card.Section>
                <Card.Section title={t("Accounts")}>
                    <FormLayout>
                        <FormLayout.Group>
                            <Toggle
                                labelRight={t("Automatically apply minimum to accounts")}
                                labelSupport={t("This minimum will be automatically applied to the selected customer types")}
                                checked={state.apply_to_new_accounts}
                                onChange={e => setState(prev => ({ ...prev, apply_to_new_accounts: e.target.checked }))}
                            />
                        </FormLayout.Group>
                        {state.apply_to_new_accounts ? (
                            <FormLayout.Group>
                                <div className="ml-15">
                                    <p className="loyalty-settings-title">{t("Customer Types")}:</p>
                                    {types?.map((type, index) => {
                                        return (
                                            <div key={index}>
                                                <Checkbox 
                                                    size="small" 
                                                    label={`${type?.title} ${type?.subtitle ? `- ${type.subtitle}` : ""}`} 
                                                    onChange={() => addType(type)} 
                                                    checked={state.apply_to_new_account_customer_type_ids?.includes(type?.id)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </FormLayout.Group>
                        ) : null }
                    </FormLayout>
                </Card.Section>
                <Card.Section title={t("Qualified Departments")}>
                    <FormLayout>
                        <FormLayout.Group>
                            <div className="ml-15">
                                {departments?.map((department, index) => {
                                    return (
                                        <div key={index}>
                                            <Checkbox size="small" label={department?.title} onChange={() => addDepartment(department)} checked={state.qualified_departments?.includes(department?.id)}/>
                                            {state.qualified_departments?.includes(department?.id) && department?.cateogries?.length > 0 ? (
                                            department?.cateogries?.map((category, categoryIndex) => {
                                                return (
                                                <div style={{textIndent: "10px"}} key={categoryIndex}>
                                                    <Checkbox size="small" label={category?.title} onChange={() => addDepartment(category)} checked={state.qualified_departments?.includes(category?.id)}/>
                                                </div>
                                                );
                                            })
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </FormLayout.Group>
                    </FormLayout>
                </Card.Section>
            </Card>
        </Page>
    );
}